/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import {
  EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE,
  EMAIL_SIGNATURE_WIDTH,
  EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE,
  EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE,
  EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE,
  EM_SIG_IMAGES_SIZES_DICT,
  ENVIRONMENTAL_FOOTER_COLOR,
  ENVIRONMENTAL_FOOTER_OPTIONS,
} from '@components/EmailSignaturev2/consts';
import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
// components
import { faLeaf } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// hooks
import useNode from '@hooks/use-node';
import { getImageProxyLink } from '@modules/email-signatures-helpers';
// utils
import Emitter from '@modules/event-emitter';
import { sortBy } from 'es-toolkit';
import { AnimatePresence, motion } from 'motion/react';
import { type FC, useEffect, useMemo } from 'react';

import styles from './EmailSignatureEnvironmentalFooter.module.scss';

interface EmailSignatureEnvironmentalFooterProps {
  emailSignature: EmailSignatureV2;
  disableAllSpecialEffects: boolean;
  showDummyBanner?: boolean;
  isInEditingMode?: boolean;
}

const EmailSignatureEnvironmentalFooter: FC<
  React.PropsWithChildren<EmailSignatureEnvironmentalFooterProps>
> = ({ emailSignature, disableAllSpecialEffects, showDummyBanner, isInEditingMode }) => {
  const [environmentalFooterNode, environmentalFooterNodeRef] = useNode<HTMLTableElement>();

  // if environmental footer is the first banner right below the social links, we add extra padding to the top
  const addExtraPaddingTopIfFirstBanner = useMemo(() => {
    if (emailSignature.banner || showDummyBanner) {
      return false;
    } else {
      return true;
    }
  }, [emailSignature.banner, showDummyBanner]);

  const environmentalWidth = useMemo(() => {
    const images = emailSignature.images.filter((image) => image.imageUrl);
    if (images.length === 0) {
      return EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE;
    } else {
      const biggestImage = sortBy(images, [
        (image) => EM_SIG_IMAGES_SIZES_DICT[image.size] * -1,
      ])[0];
      if (biggestImage.size === 'small') {
        return EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE;
      } else if (biggestImage.size === 'medium') {
        return EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE;
      } else {
        return EMAIL_SIGNATURE_WIDTH;
      }
    }
  }, [emailSignature.images]);

  const imageLink = useMemo(() => {
    if (isInEditingMode) {
      return emailSignature.environmentalFooter?.iconUrl;
    } else {
      // if it is for testing, use firebase url since the admin may have not saved the signature and the proxy links have not been created yet
      return getImageProxyLink(emailSignature.environmentalFooter?.id || '');
    }
  }, [
    emailSignature.environmentalFooter?.iconUrl,
    emailSignature.environmentalFooter?.id,
    isInEditingMode,
  ]);

  useEffect(() => {
    if (!environmentalFooterNode) return;
    // scroll into view when the footer is first created
    environmentalFooterNode.scrollIntoView();
  }, [environmentalFooterNode]);

  return (
    <>
      <tr>
        <td
          style={{
            height: addExtraPaddingTopIfFirstBanner ? '28px' : '15px',
            width: '600px',
          }}
        ></td>
      </tr>
      <tr>
        <td
          role={disableAllSpecialEffects ? undefined : 'button'}
          tabIndex={disableAllSpecialEffects ? undefined : 0}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              Emitter.emit('SCROLL_TO_ENVIRONMENTAL_FOOTER');
            } else if (event.key === ' ') {
              event.preventDefault();
            }
          }}
          onClick={() => {
            Emitter.emit('SCROLL_TO_ENVIRONMENTAL_FOOTER');
          }}
        >
          <table
            ref={environmentalFooterNodeRef}
            cellPadding={0}
            cellSpacing={0}
            className={
              disableAllSpecialEffects ? undefined : styles['interactive-environmental-footer']
            }
            style={{ width: `${environmentalWidth}px` }}
          >
            <tbody>
              <tr>
                <td
                  width={'1%'}
                  style={{
                    paddingRight: '5px',
                    lineHeight: `${EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE}px`,
                    verticalAlign: 'middle',
                    borderRight: '1px solid #e5e5e5',
                  }}
                >
                  <img
                    style={{
                      marginBottom: '-12px',
                    }}
                    width={`${EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE}px`}
                    height={`${EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE}px`}
                    src={imageLink}
                    alt="Green"
                  />
                </td>
                <td
                  style={{
                    color: ENVIRONMENTAL_FOOTER_COLOR,
                    fontSize: '14px',
                    verticalAlign: 'middle',
                    fontFamily: emailSignature.design.font,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    paddingLeft: '10px',
                  }}
                >
                  {emailSignature.environmentalFooter?.value ?? ''}
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </>
  );
};

type EmailSignatureDummyEnvironmentalFooterProps = {
  emailSignature: EmailSignatureV2;
  show: boolean;
};

const EmailSignatureDummyEnvironmentalFooter: FC<
  React.PropsWithChildren<EmailSignatureDummyEnvironmentalFooterProps>
> = ({ emailSignature, show }) => {
  const [environmentalFooterNode, environmentalFooterNodeRef] = useNode<HTMLTableElement>();

  const environmentalWidth = useMemo(() => {
    const images = emailSignature.images.filter((image) => image.imageUrl);
    if (images.length === 0) {
      return EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE;
    } else {
      const biggestImage = sortBy(images, [
        (image) => EM_SIG_IMAGES_SIZES_DICT[image.size] * -1,
      ])[0];
      if (biggestImage.size === 'small') {
        return EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE;
      } else if (biggestImage.size === 'medium') {
        return EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE;
      } else {
        return EMAIL_SIGNATURE_WIDTH;
      }
    }
  }, [emailSignature.images]);

  // if environmental footer is the first banner right below the social links, we add extra padding to the top
  const addExtraPaddingTopIfFirstBanner = useMemo(() => {
    if (emailSignature.banner) {
      return false;
    } else {
      return true;
    }
  }, [emailSignature.banner]);

  useEffect(() => {
    if (!environmentalFooterNode) return;
    environmentalFooterNode.scrollIntoView();
  }, [environmentalFooterNode]);

  return (
    <AnimatePresence>
      {show && (
        <>
          <tr>
            <td
              style={{
                height: addExtraPaddingTopIfFirstBanner ? '28px' : '15px',
                width: `${environmentalWidth}px`,
              }}
            ></td>
          </tr>
          <tr>
            <td>
              <motion.table
                ref={environmentalFooterNodeRef}
                cellPadding={0}
                cellSpacing={0}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
              >
                <tbody>
                  <tr>
                    <td
                      width={'1%'}
                      style={{
                        paddingRight: '5px',
                        lineHeight: `${EMAIL_SIGNATURE_ENVIRONMENTAL_FOOTER_ICON_SIZE}px`,
                        verticalAlign: 'middle',
                        borderRight: '1px solid #e5e5e5',
                        fontSize: '0.8rem',
                      }}
                    >
                      <div className={styles['environmental-dummy-icon-container']}>
                        <FontAwesomeIcon
                          icon={faLeaf}
                          style={{
                            color: ENVIRONMENTAL_FOOTER_COLOR,
                          }}
                          size={'lg'}
                        />
                      </div>
                    </td>
                    <td
                      style={{
                        color: ENVIRONMENTAL_FOOTER_COLOR,
                        fontSize: '14px',
                        verticalAlign: 'middle',
                        fontFamily: emailSignature.design.font,
                        wordBreak: 'break-word',
                        overflowWrap: 'word-break',
                        paddingLeft: '10px',
                      }}
                    >
                      {ENVIRONMENTAL_FOOTER_OPTIONS[0].value}
                    </td>
                  </tr>
                </tbody>
              </motion.table>
            </td>
          </tr>
        </>
      )}
    </AnimatePresence>
  );
};

export default EmailSignatureEnvironmentalFooter;

export { EmailSignatureDummyEnvironmentalFooter };
